@file:UseSerializers(BigDecimalSerializer::class)
@file:OptIn(ExperimentalTime::class)

package net.sergeych.ugdx.shared

import com.ionspin.kotlin.bignum.decimal.BigDecimal
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.serialization.Serializable
import kotlinx.serialization.Transient
import kotlinx.serialization.UseSerializers
import net.sergeych.crypto2.SealedBox
import net.sergeych.crypto2.SigningSecretKey
import net.sergeych.kilogin.Safe58
import net.sergeych.kilogin.biDecode
import net.sergeych.kilogin.randomBytes
import net.sergeych.mptools.bd
import net.sergeych.ugdx.GuestAPI
import net.sergeych.ugdx.Party
import net.sergeych.ugdx.shared.ErrorCode.INVALID_INVOICE
import net.sergeych.ugdx.shared.ErrorCode.NOT_FOUND
import net.sergeych.ugdx.shared.api.*
import kotlin.time.Duration.Companion.days
import kotlin.time.ExperimentalTime

@Serializable
data class Invoice(
    val issuerPartyId: String,
    val issuerAccountId: Long,
    val issuedToPartyId: String,
    val total: BigDecimal,
    val currencyCode: String,
    val title: String,
    val items: List<Item>,
    val extraPayments: List<ExtraPayment>,
    val issuedAt: Instant,
    val dueBy: Instant,
    val description: String? = null,
    val uniqueId: String = Safe58.random(8),
) {

    suspend fun issuedBy() = GuestAPI().account.getParty(issuerPartyId) ?: NOT_FOUND.raise("Party not found")
    suspend fun issuedTo() = GuestAPI().account.getParty(issuedToPartyId) ?: NOT_FOUND.raise("Party not found")

    @Serializable
    data class Item(
        val name: String,
        val qty: BigDecimal,
        val price: BigDecimal,
        val amount: BigDecimal,
    )

    @Serializable
    data class ExtraPayment(val title: String, val value: BigDecimal)

    val isCorrect by lazy {
        var sum = 0.bd
        for (i in items) {
            if (i.qty * i.price != i.amount) return@lazy false
            sum += i.amount
        }
        for (x in extraPayments) {
            sum += x.value
        }
        sum == total
    }


    companion object {
        fun create(
            title: String,
            issuerPartyId: String,
            issuerAccountId: Long,
            customerPartyId: String,
            currencyCode: String,
            items: List<Item>,
            extras: List<ExtraPayment> = listOf(),
            issuedAt: Instant = Clock.System.now(),
            dueBy: Instant = issuedAt.plus(15.days),
        ): Invoice {
            var sum = 0.bd
            for (i in items) sum += i.amount
            for (x in extras) sum += x.value
            return Invoice(issuerPartyId,
                issuerAccountId,
                customerPartyId,
                sum,
                currencyCode,
                title,
                items,
                extras,
                issuedAt,
                dueBy)
        }
    }

    override fun toString(): String {
        return "Invoice by $issuerPartyId to $issuedToPartyId: $title : ${total.toPlainString()} $currencyCode"
    }
}

private fun Safe58.random(byteCount: Int=8): String = encode(randomBytes(byteCount))


@Serializable
class SignedInvoice(
    val packedSignedRecord: UByteArray,
    val operation: ApiOperation? = null,
    val rejectedAt: Instant? = null,
    val rejectedByCustomer: Boolean? = null,
    val createdAt: Instant = Clock.System.now(),
) {

    val isRejected by lazy { rejectedAt != null }

    val isPaid by lazy { operation != null }

    val paidAt by lazy { operation?.createdAt }

    @Transient
    var signedRecord: SealedBox? = null

    /**
     * Decode invoice without checking signature, and check its items/extras/total integrity.
     */
    val invoice: Invoice by lazy {
        val sr = biDecode<SealedBox>(packedSignedRecord)
        val i = sr.unpack<Invoice>()
        if (!i.isCorrect) INVALID_INVOICE.raise("total amount incorrect")
        signedRecord = sr
        i
    }

    val uniqueId by lazy { invoice.uniqueId }

    /**
     * Get the issuing party and check its signature is valid and belongs to it.
     */
    suspend fun checkedInvoice(): Invoice {
        val i = invoice
        if (!i.isCorrect) INVALID_INVOICE.raise("total amount incorrect")
        checkIssuerParty(invoice.issuedBy())
        return i
    }

    /**
     * Check that invoice is properly signed by the party
     */
    suspend fun checkIssuerParty(issuedBy: Party) {
        val i = invoice
        val sr = signedRecord!!
        if (issuedBy.id != i.issuerPartyId)
            INVALID_INVOICE.raise("invoice partyId does not match")
        val issuedByKey = issuedBy.publicKey()
        if (issuedByKey.verifyingKey !in sr.signedByKeys)
            INVALID_INVOICE.raise("signed by wrong party, issuer signature required")
        issuedBy.overridePublicKey(issuedByKey)
    }

    suspend fun refresh(): SignedInvoice = cmdInvoiceGet(InvoiceIdArgs(invoice.uniqueId))

    suspend fun pay(account: ApiAccount): SignedInvoice {
        val order = InvoicePaymentOrder(account.id, invoice.total, "Payment for invoice $uniqueId", uniqueId )
        order.submit()
        return refresh()
    }

    suspend fun reject() {
        cmdInvoiceReject(InvoiceIdArgs(invoice.uniqueId))
    }

    override fun equals(other: Any?): Boolean {
        return other is SignedInvoice && other.invoice == invoice && operation == other.operation &&
                paidAt == other.paidAt && rejectedAt == other.rejectedAt
    }

    override fun hashCode(): Int {
        return invoice.hashCode() + paidAt.hashCode() + rejectedAt.hashCode()
    }

    override fun toString(): String {
        return "Signed $invoice"
    }

    companion object {

        /**
         * Get the invoice by its `uniqueId`
         */
        suspend fun get(uniqueId: String): SignedInvoice? = try {
            cmdInvoiceGet(InvoiceIdArgs(uniqueId))
        } catch (x: ApiException) {
            if (x.code == NOT_FOUND.name) null
            else throw x
        }

        /**
         * Request invoices in the specified period filtering by state. Important note: service may return less
         * invoices than requested and exist in the period, see [InvoiceListResult] for more info on how to
         * interpret it.
         *
         * We recommend to use [all] instead as it performs all necessary actions to get a list of all invoices in
         * the interval.
         *
         * @param since the earliest instant to include, inclusive
         * @param until the oldest instant to include, exclusive
         * @param showNew include not paid and not rejected invoices
         * @param showRejected include rejected invoices
         * @param showPaid include paid invoices
         * @param limit do not include more than limit invoices (there could be less even if there are more invoices
         *              in the selected period, see [InvoiceListResult] for more.
         * @return list (likely partial!) of the invoices in the period. Invoices are sorted as most recent first
         *
         */
        suspend fun get(
            since: Instant, until: Instant,
            showNew: Boolean = true,
            showPaid: Boolean = true,
            showRejected: Boolean = true,
            limit: Int = 50,
        ): InvoiceListResult =
            cmdInvoiceList(InvoiceListArgs(since, until, showNew, showPaid, showRejected, limit))

        /**
         * Return _all invoices in the interval as an asynchronous flow, making as many requests the the service
         * as need. This is the preferred way of getting invoices.
         *
         * Flow is sorted mist recent first (by `createdAt`).
         *
         * @param since the earliest instant to include, inclusive
         * @param until the oldest instant to include, inclusive
         * @param showNew include not paid and not rejected invoices
         * @param showRejected include rejected invoices
         * @param showPaid include paid invoices
         * @param batchSize how many invoices to request in a single query to the service.
         */
        suspend fun all(
            since: Instant = Instant.DISTANT_PAST,
            until: Instant = Instant.DISTANT_FUTURE,
            showNew: Boolean = true,
            showPaid: Boolean = true,
            showRejected: Boolean = true, batchSize: Int = 20,
        ): Flow<SignedInvoice> {
            return flow {
                val reported = mutableSetOf<String>()
                var result: InvoiceListResult? = null
                var end = until

                do {
                    result = get(since, end, showNew, showPaid, showRejected, batchSize)
                    for (i in result.signedInvoices) {
                        val id = i.invoice.uniqueId
                        if (id !in reported) {
                            reported.add(id)
                            emit(i)
                        }
                        if (end > i.createdAt) end = i.createdAt
                    }
                    // TODO: check potentia bug if there are more invoices than batchSize with the same createdAt
                    // then it will never ends. We should track it and throw exception
                } while (result?.hasMore == true)
            }
        }


        internal suspend fun sign(key: SigningSecretKey, invoice: Invoice): SignedInvoice {
            if (!invoice.isCorrect) INVALID_INVOICE.raise("can't pack an incorrect invoice")
            println("sign with $key")
            println("sign with ${key.verifyingKey}")
            val ib = invoice.issuedBy()
            println("isigned by $ib")
            val issuerKeyId = ib.publicKey()
            println("itissued by $issuerKeyId")
            if (key.id != invoice.issuedBy().verifyingKeyId)
                INVALID_INVOICE.raise("signing key does not match party's")
            return SignedInvoice(SealedBox.encode(invoice, key))
        }
    }
}
